// File: src/components/AttachmentPreview.tsx (updated)
import React, { useState, useEffect } from 'react';
import { Image, File, Download, X, Eye, Paperclip, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';

interface AttachmentInfo {
  index: number;
  url: string;
  filename: string;
  contentType: string;
}

interface AttachmentPreviewProps {
  reportId: string;
  attachments?: string[];
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ reportId, attachments }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [attachmentInfo, setAttachmentInfo] = useState<AttachmentInfo[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Fetch attachment info on mount
  useEffect(() => {
    fetchAttachmentsInfo();
  }, [reportId]);

  const fetchAttachmentsInfo = async () => {
    setFetching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAttachmentInfo(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching attachment info:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleViewAttachment = async (index: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachment/${index}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load attachment');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setAttachmentUrl(url);
      setSelectedIndex(index);
    } catch (error) {
      console.error('Error loading attachment:', error);
      toast.error('Failed to load attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (index: number, filename?: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachment/${index}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `attachment-${index + 1}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const closeModal = () => {
    if (attachmentUrl) {
      URL.revokeObjectURL(attachmentUrl);
      setAttachmentUrl(null);
    }
    setSelectedIndex(null);
  };

  // If no attachments or still fetching, show appropriate message
  if (fetching) {
    return (
      <div className="mt-2 flex items-center space-x-1 text-xs text-gray-400">
        <Loader2 size={12} className="animate-spin" />
        <span>Loading attachments...</span>
      </div>
    );
  }

  if (!attachmentInfo || attachmentInfo.length === 0) {
    return null;
  }

  const displayAttachments = showAll ? attachmentInfo : attachmentInfo.slice(0, 3);
  const hasMore = attachmentInfo.length > 3;

  const getFileIcon = (contentType: string) => {
    if (contentType?.startsWith('image/')) return <Image size={24} className="text-blue-500" />;
    if (contentType === 'application/pdf') return <File size={24} className="text-red-500" />;
    return <File size={24} className="text-gray-500" />;
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 flex items-center">
          <Paperclip size={12} className="mr-1" />
          Attachments ({attachmentInfo.length})
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {displayAttachments.map((info, idx) => (
          <div
            key={idx}
            className="relative group w-16 h-16 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
            onClick={() => handleViewAttachment(info.index)}
          >
            <div className="w-full h-full flex items-center justify-center">
              {getFileIcon(info.contentType)}
            </div>
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <Eye size={16} className="text-white" />
            </div>
            
            {/* More indicator */}
            {idx === 2 && hasMore && !showAll && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                <span className="text-white text-xs font-bold">+{attachmentInfo.length - 3}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Show more/less button */}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-2 text-xs text-blue-600 hover:text-blue-800"
        >
          {showAll ? 'Show less' : `Show all ${attachmentInfo.length} attachments`}
        </button>
      )}

      {/* Modal for viewing attachment */}
      {selectedIndex !== null && attachmentUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="relative max-w-4xl w-full max-h-full" onClick={e => e.stopPropagation()}>
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 z-10"
            >
              <X size={24} />
            </button>
            
            {/* Content */}
            <div className="bg-white rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 size={32} className="animate-spin mx-auto text-blue-500" />
                  <p className="mt-2 text-gray-600">Loading...</p>
                </div>
              ) : (
                <>
                  {attachmentInfo[selectedIndex]?.contentType?.startsWith('image/') ? (
                    <img 
                      src={attachmentUrl} 
                      alt="Attachment" 
                      className="max-w-full max-h-[70vh] object-contain mx-auto"
                    />
                  ) : attachmentInfo[selectedIndex]?.contentType === 'application/pdf' ? (
                    <iframe 
                      src={attachmentUrl} 
                      className="w-full h-[70vh]"
                      title="PDF Attachment"
                    />
                  ) : (
                    <div className="p-8 text-center">
                      {getFileIcon(attachmentInfo[selectedIndex]?.contentType)}
                      <p className="text-gray-600 mt-4 mb-4">
                        {attachmentInfo[selectedIndex]?.filename || 'Document'}
                      </p>
                      <button
                        onClick={() => handleDownload(
                          selectedIndex, 
                          attachmentInfo[selectedIndex]?.filename
                        )}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center"
                      >
                        <Download size={16} className="mr-2" />
                        Download File
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Download button for images/PDFs */}
            {(attachmentInfo[selectedIndex]?.contentType?.startsWith('image/') || 
              attachmentInfo[selectedIndex]?.contentType === 'application/pdf') && (
              <button
                onClick={() => handleDownload(
                  selectedIndex, 
                  attachmentInfo[selectedIndex]?.filename
                )}
                className="absolute -bottom-12 right-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Download size={16} className="mr-2" />
                Download
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentPreview;